import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TenantPage from 'components/common/TenantPage';
import { contractEquipmentService } from 'services/api';
import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import {
  DataGridPremiumProps,
  GridActionsCellItem,
  GridColDef,
  GridColumns,
  GridRowParams
} from '@mui/x-data-grid-premium';
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { dateFilterOperators, filterOperators } from '../../../../constants';
import { baseCell } from 'components/common/enhanced/cells/base-cell';
import { MaintenanceColumns } from 'components/common/enhanced/common-headers';
import MaintenanceDetail from '../detail/MaintenanceDetail';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import {
  isTenantAdministrator,
  IsTenantAdministrator
} from 'components/helpers/IsTenantAdministrator';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { MaintenanceMonthDataDialog } from './MaintenanceMonthDataDialog';
import { isSupplier } from 'components/helpers/IsSupplier';
import { isTechnician } from 'components/helpers/IsTechnician';
import { isDirector } from 'components/helpers/IsDirector';
import CloseMaintenanceDialog from './CloseMaintenanceDialog';
import toast from 'features/toast';
import MultipleDatesPicker from '@ambiot/material-ui-multiple-dates-picker';

export const Maintenance = () => {
  const translationPrefix = 'pages.maintenance';

  const { t } = useTranslation();
  const datagridRefresh = useRef(null);

  const [contractEquipment, setContractEquipment] = useState<number | null>(null);
  const [searchParams] = useSearchParams();

  const archiveCanSee = isTenantAdministrator() || isDirector();

  // ----------------------------------------------------  SINGLE DATE
  const [openMultiDate, setOpenMultiDate] = useState(false);

  const onOpenMultiDateCalendar = (contractEquipmentId: number) => {
    setContractEquipment(contractEquipmentId);
    setOpenMultiDate(true);
  };

  const onCancelMultiDateCalendar = () => {
    setOpenMultiDate(false);
    setContractEquipment(null);
  };

  const onSubmitMultiDate = (values: any) => {
    const formattedDates = values.map((dateStr) => {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    });
    contractEquipmentService.addMaintenanceDates(contractEquipment, formattedDates).then(() => {
      datagridRefresh.current();
      onCancelMultiDateCalendar();
    });
  };
  // ----------------------------------------------------  MULTI MONTHs
  const [openExpectedMultiMonthCalendar, setOpenExpectedMultiMonthCalendar] = useState(false);

  const onOpenExpectedMultiMonthCalendar = (contractEquipmentId: number) => {
    setOpenExpectedMultiMonthCalendar(true);
    setContractEquipment(contractEquipmentId);
  };

  const onCancelExpectedMultiMonth = () => {
    setOpenExpectedMultiMonthCalendar(false);
    setContractEquipment(null);
  };

  const onSubmitMultipleMonth = async (values) => {
    const { month, rep_frequency, rep_unit, end_programmed } = values;
    const formattedMonth = dayjs(month).format('YYYY-MM-DD');
    const formattedEndProgrammed = end_programmed
      ? dayjs(end_programmed).format('YYYY-MM-DD')
      : null;

    contractEquipmentService
      .addProgrammedMaintenanceMonth(
        contractEquipment,
        formattedMonth,
        rep_frequency,
        rep_unit,
        formattedEndProgrammed
      )
      .then(() => {
        toast.success(t(`actions.successfully.created`));
        setContractEquipment(null);
        datagridRefresh.current();
      });
    setOpenExpectedMultiMonthCalendar(false);
  };

  // ---------------------------------------------------- Close Maintenance Dialog
  const [openCloseMaintenance, setOpenCloseMaintenance] = useState(false);
  const [maintenanceCloseMaintenanceId, setMaintenanceCloseMaintenanceId] = useState<number | null>(
    null
  );

  const setOpenCloseMaintenanceDialog = (id: number) => {
    setOpenCloseMaintenance(true);
    setMaintenanceCloseMaintenanceId(id);
  };

  const setCloseMaintenanceDialog = () => {
    setOpenCloseMaintenance(false);
    setMaintenanceCloseMaintenanceId(null);
  };

  // ---------------------------------------------------- Maintenance Detail and Columns
  const isDisabledEdit = isTechnician() || isDirector() || isSupplier();

  const aggregatedColumns: GridColumns = [
    {
      field: 'equipment',
      headerName: t(`global.equipment`),
      width: 250,
      filterOperators,
      valueGetter: (params) => params.row.equipment_code,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      width: 300,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'serial_number',
      headerName: t(`entities.equipment.serial_number`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'warehouse_code',
      headerName: t(`global.warehouse`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'position',
      headerName: t(`entities.equipment.position`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'expected_month',
      headerName: t(`entities.equipment.expected_month`),
      width: 200,
      filterOperators: dateFilterOperators,
      renderCell: (params) => {
        const dateStr = params.value;
        if (!dateStr) return '';

        // Gestione formato ISO: 'yyyy-mm-dd'
        const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
        if (isoMatch) {
          const [, year, month] = isoMatch;
          return `${month}/${year}`;
        }

        // Altrimenti, fallback su 'dd/mm/yyyy'
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const month = parts[1];
          const year = parts[2];
          return `${month}/${year}`;
        }

        // Fallback: ritorna stringa originale
        return dateStr;
      }
    }
  ];
  if (!isDisabledEdit) {
    aggregatedColumns.push({
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        const actions = [
          <GridActionsCellItem
            key={1}
            label="Calendario"
            icon={<EventAvailableIcon />}
            onClick={() => onOpenExpectedMultiMonthCalendar(params.row.contract_equipment_id)}
          />,
          <GridActionsCellItem
            key={2}
            icon={<ScheduleIcon />}
            label={t(`${translationPrefix}.close`)}
            onClick={() => onOpenMultiDateCalendar(params.row.contract_equipment_id)}
          />
        ];
        return actions;
      },
      filterable: false
    });
  }

  const columns: GridColDef[] = MaintenanceColumns(setOpenCloseMaintenanceDialog);

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridPremiumProps['getDetailPanelContent']>
  >(({ row }) => <MaintenanceDetail row={{ ...row }} columns={columns} />, [columns]);

  return (
    <>
      <TenantPage
        title={t(`${translationPrefix}.title`)}
        subtitle={t(`${translationPrefix}.subtitle`)}
        menuRight={
          <div className="flex justify-end items-center">
            <ButtonGroup>
              <Link to="expiring">
                <Button variant="outlined" size="small">
                  {t(`${translationPrefix}.expiring`)}
                </Button>
              </Link>
              {archiveCanSee && (
                <Link to="archive">
                  <Button variant="outlined" size="small">
                    {t(`global.archive`)}
                  </Button>
                </Link>
              )}
            </ButtonGroup>
          </div>
        }>
        <EnhancedDataGrid
          columns={aggregatedColumns}
          service={contractEquipmentService}
          getMethod={contractEquipmentService.aggregatedContract}
          initialParams={{ equipment_id: searchParams.get('equipment_id') }}
          refresh={datagridRefresh}
          getDetailPanelContent={getDetailPanelContent}
          disableDelete
        />
      </TenantPage>
      <CloseMaintenanceDialog
        closeDialog={setCloseMaintenanceDialog}
        datagridRefresh={datagridRefresh}
        id={maintenanceCloseMaintenanceId}
        open={openCloseMaintenance}
      />
      {/* MULTI MONTH DIALOG*/}
      <MaintenanceMonthDataDialog
        openCalendar={openExpectedMultiMonthCalendar}
        onCancel={onCancelExpectedMultiMonth}
        onSubmit={onSubmitMultipleMonth}
      />

      <MultipleDatesPicker
        open={openMultiDate}
        onCancel={onCancelMultiDateCalendar}
        onSubmit={onSubmitMultiDate}
      />
    </>
  );
};

export default Maintenance;
